/**
 * Dev mode: whether this device shows the dev tools (docs/DEPLOYMENT.md, "Dev mode").
 *
 * The tools ship in every build, as lazily loaded chunks a player never downloads. They
 * show only once dev mode is on: on by default in `pnpm dev` (Vite's dev server), off
 * everywhere else until someone taps Nib on the You page (the stats screen) {@link TAPS_TO_ENABLE} times.
 * The choice is remembered per device (`arrow:v1:dev:mode`); the dev panel turns it off.
 *
 * The dev-tools token (`arrow:v1:dev:token`), if one is stored, is the api's `DEV_TOOLS_TOKEN`.
 * The panel no longer asks for it (30 Oct 2026: dev mode alone opens /v1/dev/puzzles); only
 * /v1/dev/analytics needs it. It never leaves the device except in that header, to the api.
 *
 * Kept small and free of the panel's code: this module is in the main bundle.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { tokenMs } from '../app/motion.ts';
import { browserStore, type KeyValueStore } from '../storage/kv.ts';

export const DEV_MODE_KEY = 'arrow:v1:dev:mode';
export const DEV_TOKEN_KEY = 'arrow:v1:dev:token';

/** Taps on Nib (Stats) that turn dev mode on. */
export const TAPS_TO_ENABLE = 12;
/** A longer pause between taps starts the count again. */
export const TAP_GAP_MS = 1500;
/** The countdown shows for the last few taps only. */
export const TAP_HINT_FROM = 4;

const EVENT = 'arrow:dev-mode';

/** Whether dev mode is on: the device's choice, else on in the dev server only. */
export function devModeOn(store: KeyValueStore = browserStore(), fallback: boolean = import.meta.env.DEV): boolean {
	const value = store.get(DEV_MODE_KEY);
	if (value === 'on') return true;
	if (value === 'off') return false;
	return fallback;
}

export function setDevMode(on: boolean, store: KeyValueStore = browserStore()): void {
	store.set(DEV_MODE_KEY, on ? 'on' : 'off');
	if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
}

function subscribe(onChange: () => void): () => void {
	const onStorage = (e: StorageEvent) => {
		if (e.key === null || e.key === DEV_MODE_KEY) onChange();
	};
	window.addEventListener(EVENT, onChange);
	window.addEventListener('storage', onStorage);
	return () => {
		window.removeEventListener(EVENT, onChange);
		window.removeEventListener('storage', onStorage);
	};
}

/** Dev mode, live: re-renders when it's switched (here or in another tab). */
export function useDevMode(): boolean {
	return useSyncExternalStore(subscribe, () => devModeOn(), () => false);
}

/** The pasted dev-tools token, or null. */
export function devToolsToken(store: KeyValueStore = browserStore()): string | null {
	return store.get(DEV_TOKEN_KEY)?.trim() || null;
}

export function setDevToolsToken(token: string | null, store: KeyValueStore = browserStore()): void {
	const value = token?.trim();
	if (value) store.set(DEV_TOKEN_KEY, value);
	else store.remove(DEV_TOKEN_KEY);
}

/** The Nib tap count: taps so far in this run, and when the last one was. */
export interface TapRun {
	count: number;
	at: number;
}

export const NO_TAPS: TapRun = { count: 0, at: 0 };

/**
 * One more tap at `now`. `remaining` is how many taps are still needed (0: turn dev mode
 * on, and the run starts over). A pause longer than {@link TAP_GAP_MS} restarts the count.
 */
export function tapNib(run: TapRun, now: number): { run: TapRun; remaining: number } {
	const count = run.count > 0 && now - run.at <= TAP_GAP_MS ? run.count + 1 : 1;
	const remaining = TAPS_TO_ENABLE - count;
	return remaining <= 0 ? { run: NO_TAPS, remaining: 0 } : { run: { count, at: now }, remaining };
}

/** What to say after a tap, if anything: quiet until the last few. */
export function tapHint(remaining: number, alreadyOn: boolean): string | null {
	if (remaining === 0) return alreadyOn ? 'Dev mode is already on.' : 'Dev mode on. Open a puzzle for the dev tools.';
	if (remaining > TAP_HINT_FROM) return null;
	return `${remaining} more ${remaining === 1 ? 'tap' : 'taps'}`;
}

/** How long a countdown note ("3 more taps") stays when no tap follows it. */
const COUNTDOWN_NOTE_MS = 1400;

/**
 * Nib's secret (Stats): call `tap` on each tap. Quiet for the first taps, a countdown for
 * the last few, then dev mode turns on with a note. `note` is what to show (null: nothing),
 * `final` marks the last one, and `serial` changes with each note to restart its animation.
 */
export function useNibTaps(now: () => number = Date.now): { tap: () => void; note: string | null; final: boolean; serial: number } {
	const run = useRef<TapRun>(NO_TAPS);
	const [shown, setShown] = useState<{ note: string | null; final: boolean; serial: number }>({ note: null, final: false, serial: 0 });
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	useEffect(() => () => clearTimeout(timer.current), []);
	const tap = useCallback(() => {
		const next = tapNib(run.current, now());
		run.current = next.run;
		const alreadyOn = devModeOn();
		const note = tapHint(next.remaining, alreadyOn);
		if (next.remaining === 0 && !alreadyOn) setDevMode(true);
		if (note === null) return;
		setShown((cur) => ({ note, final: next.remaining === 0, serial: cur.serial + 1 }));
		clearTimeout(timer.current);
		// The countdown's notes step aside for the next tap; the last one stays as long as any
		// toast does (it's drawn by the shared Toast, which fades over --dur-toast).
		const stay = next.remaining === 0 ? tokenMs('--dur-toast', 2400) : COUNTDOWN_NOTE_MS;
		timer.current = setTimeout(() => setShown((cur) => ({ ...cur, note: null })), stay);
	}, [now]);
	return { tap, ...shown };
}
